"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { Button } from "@/shared/ui/buttons/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/ui/data-display/avatar";
import { Skeleton } from "@/shared/ui/data-display/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/shared/ui/modals/dialog";
import { useGetTeamsTeamidInvitations } from "@/shared/api/generated-kubb/hooks/useGetTeamsTeamidInvitations";
import { extractApiErrorMessage } from "@/shared/api/error-handler";
import { useFriends, useFriendsPresence, useInviteFriendToTeam } from "@/hooks/useFriends";
import { useTeam } from "@/hooks/useRosters";
import { cn } from "@/shared/lib/utils";
import { BOARD_DIALOG_CLASS, BOARD_DIALOG_HEADER, DialogBody } from "./dialog-body";
import { CardChip, LfgRoleIcon, RankLabel } from "./board-card-parts";
import { toLfgRank } from "../model/board-filters";
import {
  buildTeamSlots,
  filledCount,
  invitableFriends,
  toInvitations,
  type TeamLane,
  type TeamMemberLite,
  type TeamSlot,
} from "../model/team-slots";

const PRIMARY_BTN =
  "h-[34px] border-highlight-border bg-highlight-soft px-3.5 font-bold text-highlight hover:bg-highlight-soft/80 hover:text-highlight";

interface TeamBuilderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamId: string;
}

/** «Сбор команды» (макет: «Сбор пати»): пять позиций основной команды, приглашение друга на свободную. */
export function TeamBuilderDialog({ open, onOpenChange, teamId }: TeamBuilderDialogProps) {
  const t = useTranslations("LfgBoard");
  const team = useTeam(open ? teamId : "");
  const invitationsQuery = useGetTeamsTeamidInvitations(open ? teamId : undefined, { query: { enabled: open && !!teamId } });
  const friends = useFriends(open);
  const presence = useFriendsPresence(open);
  const invite = useInviteFriendToTeam();
  const [picking, setPicking] = useState<TeamLane | null>(null);

  const members: TeamMemberLite[] = team.data?.members ?? [];
  const invitations = toInvitations(invitationsQuery.data?.data);
  const slots = buildTeamSlots(members, invitations);
  const online = new Set((presence.data ?? []).map((p) => p.userId));
  const candidates = invitableFriends(friends.data ?? [], members, invitations);
  const loading = team.isLoading || invitationsQuery.isLoading;

  const send = (lane: TeamLane, friendId: string) => {
    invite.mutate(
      { friendId, teamId, role: lane },
      {
        onSuccess: () => setPicking(null),
        onError: (e) => toast.error(extractApiErrorMessage(e, t("teamBuilder.inviteError"))),
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setPicking(null);
        onOpenChange(next);
      }}
    >
      <DialogContent className={BOARD_DIALOG_CLASS}>
        <DialogHeader className={BOARD_DIALOG_HEADER}>
          <DialogTitle className="font-heading text-[22px]">{t("teamBuilder.title")}</DialogTitle>
          <DialogDescription className="sr-only">{t("teamBuilder.slots")}</DialogDescription>
        </DialogHeader>

        <DialogBody>
        <div className="flex items-center justify-between">
          <span className="text-[13px] text-muted-foreground">{t("teamBuilder.slots")}</span>
          <span className="font-logo text-base text-highlight" aria-label={t("teamBuilder.count", { filled: filledCount(slots) })}>
            {filledCount(slots)}/{slots.length}
          </span>
        </div>

        {loading ? (
          <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full rounded-[10px]" />)}</div>
        ) : team.isError ? (
          <p className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">{t("teamBuilder.error")}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {slots.map((slot) => (
              <li key={slot.lane} className="overflow-hidden rounded-[10px] border border-border bg-muted/20">
                <SlotRow slot={slot} picking={picking === slot.lane} onToggle={() => setPicking(picking === slot.lane ? null : slot.lane)} />
                {picking === slot.lane && slot.state === "free" && (
                  <div className="px-3 pb-3">
                    <h3 className="mb-2 text-[11px] font-bold uppercase tracking-[1.2px] text-muted-foreground">{t("teamBuilder.fromFriends")}</h3>
                    {friends.isLoading ? (
                      <Skeleton className="h-9 w-full" />
                    ) : candidates.length === 0 ? (
                      <p className="text-xs text-muted-foreground">{t("teamBuilder.noFriends")}</p>
                    ) : (
                      <ul className="flex max-h-56 flex-col gap-1.5 overflow-y-auto">
                        {candidates.map((f) => (
                          <li key={f.id} className="flex items-center gap-2.5 rounded-lg bg-muted/40 px-2 py-1.5">
                            <span
                              aria-label={online.has(f.id) ? t("teamBuilder.online") : t("teamBuilder.offline")}
                              className={cn("size-2 flex-none rounded-full", online.has(f.id) ? "bg-score-win" : "bg-muted-foreground/50")}
                            />
                            <Avatar className="size-[26px] flex-none">
                              <AvatarImage src={f.avatarUrl ?? undefined} alt="" />
                              <AvatarFallback className="font-logo text-[10px]">{f.displayName.slice(0, 2).toUpperCase()}</AvatarFallback>
                            </Avatar>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-xs font-bold">{f.displayName}</p>
                              {/* Подсказка — только от заявленного на доске: роль в профиле необязательна. */}
                              {(f.lfgRoles ?? []).length > 0 && (
                                <p className={cn("text-[11px]", f.lfgRoles!.includes(slot.lane) ? "text-score-win" : "text-muted-foreground")}>
                                  {f.lfgRoles!.includes(slot.lane)
                                    ? t("teamBuilder.fitsRole", { role: t(`role.${slot.lane}`).toLowerCase() })
                                    : t("teamBuilder.otherRole")}
                                </p>
                              )}
                            </div>
                            {f.rank && (
                              <CardChip>
                                <RankLabel rank={toLfgRank(f.rank)} />
                              </CardChip>
                            )}
                            <Button
                              variant="outline"
                              disabled={invite.isPending}
                              className="h-7 border-highlight-border bg-highlight-soft px-2.5 text-[11px] font-bold text-highlight hover:bg-highlight-soft/80 hover:text-highlight"
                              onClick={() => send(slot.lane, f.id)}
                            >
                              {t("teamBuilder.invite")}
                            </Button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

function SlotRow({ slot, picking, onToggle }: { slot: TeamSlot; picking: boolean; onToggle: () => void }) {
  const t = useTranslations("LfgBoard");
  return (
    <div className="flex items-center gap-3 px-3 py-2.5">
      <LfgRoleIcon role={slot.lane} size={20} />
      <span className="w-20 flex-none text-[13px] font-bold">{t(`role.${slot.lane}`)}</span>
      <div className="flex min-w-0 flex-1 items-center gap-2 text-[13px]">
        {slot.state === "filled" && (
          <>
            <Avatar className="size-6 flex-none">
              <AvatarImage src={slot.avatarUrl ?? undefined} alt="" />
              <AvatarFallback className="font-logo text-[10px]">{slot.name.slice(0, 2).toUpperCase()}</AvatarFallback>
            </Avatar>
            <span className="truncate">{slot.name}</span>
          </>
        )}
        {slot.state === "invited" && (
          <>
            <span className="truncate">{slot.name}</span>
            <CardChip className="text-score-win">{t("teamBuilder.invited")}</CardChip>
          </>
        )}
        {slot.state === "free" && <span className="text-muted-foreground">{t("teamBuilder.freeSlot")}</span>}
      </div>
      {slot.state === "free" && (
        <Button
          variant="outline"
          className={picking ? "h-[34px] text-muted-foreground" : PRIMARY_BTN}
          aria-expanded={picking}
          onClick={onToggle}
        >
          {picking ? t("card.cancel") : t("teamBuilder.call")}
        </Button>
      )}
    </div>
  );
}
